import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { Hunt, huntService } from '../services/huntService';

const HuntList: React.FC = () => {
  const [hunts, setHunts] = useState<Hunt[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; hunt?: Hunt }>({ open: false });
  const [viewDialog, setViewDialog] = useState<{ open: boolean; hunt?: Hunt }>({ open: false });
  const navigate = useNavigate();

  useEffect(() => {
    loadHunts();
  }, []);

  const loadHunts = async () => {
    try {
      setLoading(true);
      const data = await huntService.getAllHunts({ limit: 100 });
      setHunts(data);
    } catch (error) {
      console.error('Error loading hunts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.hunt) return;

    try {
      await huntService.deleteHunt(deleteDialog.hunt.id);
      setDeleteDialog({ open: false });
      loadHunts();
    } catch (error) {
      console.error('Error deleting hunt:', error);
      alert('Failed to delete hunt');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Hunts</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/hunts/new')}
        >
          Create Hunt
        </Button>
      </Box>

      {hunts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="textSecondary">
              No hunts found. Create your first hunt!
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Stops</TableCell>
                <TableCell>Duration (min)</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hunts.map((hunt) => (
                <TableRow 
                  key={hunt.id}
                  hover
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell onClick={() => setViewDialog({ open: true, hunt })}>
                    <Typography variant="body1">{hunt.title}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {hunt.description?.substring(0, 50)}...
                    </Typography>
                  </TableCell>
                  <TableCell onClick={() => setViewDialog({ open: true, hunt })}>
                    {hunt.category || 'N/A'}
                  </TableCell>
                  <TableCell onClick={() => setViewDialog({ open: true, hunt })}>
                    <Chip label={hunt.difficulty} size="small" />
                  </TableCell>
                  <TableCell onClick={() => setViewDialog({ open: true, hunt })}>
                    <Chip 
                      label={hunt.status} 
                      size="small" 
                      color={getStatusColor(hunt.status)} 
                    />
                  </TableCell>
                  <TableCell onClick={() => setViewDialog({ open: true, hunt })}>
                    {hunt.locations?.length || 0}
                  </TableCell>
                  <TableCell onClick={() => setViewDialog({ open: true, hunt })}>
                    {hunt.estimatedDuration || 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/hunts/${hunt.id}/edit`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, hunt })}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Hunt Dialog */}
      <Dialog 
        open={viewDialog.open} 
        onClose={() => setViewDialog({ open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{viewDialog.hunt?.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              <strong>Description:</strong> {viewDialog.hunt?.description}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Category:</strong> {viewDialog.hunt?.category || 'N/A'}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Difficulty:</strong> {viewDialog.hunt?.difficulty}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Status:</strong> {viewDialog.hunt?.status}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Duration:</strong> {viewDialog.hunt?.estimatedDuration} minutes
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Stops:</strong> {viewDialog.hunt?.locations?.length || 0}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Completion Count:</strong> {viewDialog.hunt?.completionCount || 0}
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Average Rating:</strong> {viewDialog.hunt?.averageRating?.toFixed(1) || 'N/A'}
            </Typography>
            {viewDialog.hunt?.locations && viewDialog.hunt.locations.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>Locations:</Typography>
                {viewDialog.hunt.locations.map((loc, idx) => (
                  <Typography key={loc.id} variant="body2">
                    {idx + 1}. {loc.locationData?.name || `Location ${idx + 1}`}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false })}>Close</Button>
          <Button 
            onClick={() => {
              setViewDialog({ open: false });
              navigate(`/hunts/${viewDialog.hunt?.id}/edit`);
            }}
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })}>
        <DialogTitle>Delete Hunt</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.hunt?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HuntList;
